//@flow
import React, { PureComponent } from 'react';
import { Button } from 'react-toolbox/lib/button';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import theme from './PjpDashboardConfig.scss';
import { compose } from 'recompose';
import { XyzzyDialog } from 'common/components/common/XyzzyConfigDialog';
import { CardTab, CardTabs } from 'common/components/core/tabs';
import { PjPlanConfiguration } from 'common/models/internal/PjPlanConfiguration';

type Props = {
    configuration: PjPlanConfiguration,
    onSubmit: Object => void,
    changePjpConfig: () => void,
    t: () => void,
    getUpdatedConfig: () => void,
    isLoadingSchedulingConfigs: boolean
};

type State = {
    editedValue: ?string,
    error: ?string,
    activeTab: ?string,
    serializedSchedulingConfig?: string,
    serializedVaeConfig?: string,
    distributionPlanConfig?: string,
    defaultSerializedSchedulingConfig?: string
};

export const PjpConfigScreens = [
    'serializedSchedulingConfig',
    'serializedVaeConfig',
    'distributionPlanConfig'
];

class PjpDashboardConfig extends PureComponent<Props, State> {
    constructor(props) {
        super(props);
        this.state = {
            error: null,
            activeTab: PjpConfigScreens[0],
            activeTabIndex: 0,
            serializedSchedulingConfig:
                props.configuration?.serializedSchedulingConfig || null,
            serializedVaeConfig:
                props.configuration?.serializedVaeConfig || null,
            distributionPlanConfig:
                props.configuration?.distributionPlanConfiguration
                    ?.serializedVaeConfig || null,
            defaultSerializedSchedulingConfig:
                props.configuration?.serializedSchedulingConfig || null
        };
    }

    componentDidMount() {
        const { configuration, getUpdatedConfig } = this.props;
        const { configId } = configuration.schedulingConfigId;
        if (configId && getUpdatedConfig) {
            getUpdatedConfig(configId, configuration);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            serializedSchedulingConfig: prevSerializedSchedulingConfig
        } = prevProps.configuration;
        const { serializedSchedulingConfig } = this.props.configuration;

        if (
            serializedSchedulingConfig &&
            prevProps.configuration.schedulingConfigId.configId !==
                this.props.configuration.schedulingConfigId.configId
        ) {
            // Set everything to null when the config selection changes
            const parsedSchedulingConfig = JSON.stringify(
                JSON.parse(serializedSchedulingConfig),
                null,
                4
            );
            this.setState({
                error: null,
                defaultSerializedSchedulingConfig: parsedSchedulingConfig,
                serializedSchedulingConfig: parsedSchedulingConfig,
                serializedVaeConfig: null,
                distributionPlanConfig: null
            });
        } else {
            if (
                !prevSerializedSchedulingConfig &&
                serializedSchedulingConfig !== prevSerializedSchedulingConfig
            ) {
                const parsedSchedulingConfig = JSON.stringify(
                    JSON.parse(serializedSchedulingConfig),
                    null,
                    4
                );
                // Setting default only when the config resolves
                if (this.state.defaultSerializedSchedulingConfig === null) {
                    this.setState({
                        defaultSerializedSchedulingConfig: parsedSchedulingConfig,
                        serializedSchedulingConfig: parsedSchedulingConfig
                    });
                }
            } else if (
                serializedSchedulingConfig ===
                    this.state.serializedSchedulingConfig ||
                serializedSchedulingConfig !== prevSerializedSchedulingConfig
            ) {
                const parsedSchedulingConfig = JSON.stringify(
                    JSON.parse(serializedSchedulingConfig),
                    null,
                    4
                );
                this.setState({
                    serializedSchedulingConfig: parsedSchedulingConfig
                });
            }
        }
    }

    onClose = () => {
        let updatedSerializedSchedulingConfig = null;
        try {
            updatedSerializedSchedulingConfig = JSON.stringify(
                JSON.parse(this.props.configuration.serializedSchedulingConfig),
                null,
                4
            );
        } catch (e) {
            updatedSerializedSchedulingConfig = null;
        }

        let updatedSerializedVaeConfig;
        try {
            updatedSerializedVaeConfig = JSON.stringify(
                JSON.parse(this.props.configuration.serializedVaeConfig),
                null,
                4
            );
        } catch (e) {
            updatedSerializedVaeConfig = null;
        }

        let updatedDistributionPlanConfig;
        try {
            updatedDistributionPlanConfig = JSON.stringify(
                JSON.parse(
                    this.props.configuration.distributionPlanConfiguration
                        .serializedVaeConfig
                ),
                null,
                4
            );
        } catch (e) {
            updatedDistributionPlanConfig = null;
        }

        this.setState({
            serializedSchedulingConfig: updatedSerializedSchedulingConfig,
            serializedVaeConfig: updatedSerializedVaeConfig,
            distributionPlanConfig: updatedDistributionPlanConfig,
            error: null
        });
    };

    onReset = () => {
        let updatedSerializedSchedulingConfig;
        if (this.state.defaultSerializedSchedulingConfig) {
            updatedSerializedSchedulingConfig = JSON.stringify(
                JSON.parse(this.state.defaultSerializedSchedulingConfig),
                null,
                4
            );
        }
        this.setState({
            serializedSchedulingConfig:
                updatedSerializedSchedulingConfig || null,
            serializedVaeConfig: this.props.configuration?.serializedVaeConfig,
            distributionPlanConfig: this.props.configuration
                ?.distributionPlanConfiguration?.distributionPlanConfig,
            error: null
        });
    };

    onTextAreaChange = event => {
        const { activeTab } = this.state;
        switch (activeTab) {
            case PjpConfigScreens[0]:
                this.setState({
                    serializedSchedulingConfig: event.target.value,
                    error: null
                });
                break;

            case PjpConfigScreens[1]:
                this.setState({
                    serializedVaeConfig: event.target.value,
                    error: null
                });

                break;
            case PjpConfigScreens[2]:
                this.setState({
                    distributionPlanConfig: event.target.value,
                    error: null
                });
                break;
        }
    };

    onSubmit = () => {
        const { t, changePjpConfig, configuration } = this.props;
        const {
            serializedSchedulingConfig,
            serializedVaeConfig,
            distributionPlanConfig
        } = this.state;
        let isSchedulingConfigValid = true;
        let isVaeConfigValid = true;
        let isDistributionPlanValid = true;
        let updatedConfiguration = configuration;
        let parsedValue = '';
        let pjpConfigType;
        if (serializedSchedulingConfig) {
            try {
                parsedValue = JSON.parse(serializedSchedulingConfig);
            } catch (e) {
                isSchedulingConfigValid = false;
                pjpConfigType = PjpConfigScreens[0];
                this.setState({
                    error: t('pjpCreate.pjpConfig.invalidJson', {
                        jsonType: pjpConfigType
                    })
                });
            }
            if (isSchedulingConfigValid) {
                updatedConfiguration = updatedConfiguration.setSerializedSchedulingConfig(
                    JSON.stringify(parsedValue)
                );
            }
        }
        if (serializedVaeConfig) {
            try {
                parsedValue = JSON.parse(serializedVaeConfig);
            } catch (e) {
                isVaeConfigValid = false;
                pjpConfigType = PjpConfigScreens[1];

                this.setState({
                    error: t('pjpCreate.pjpConfig.invalidJson', {
                        jsonType: pjpConfigType
                    })
                });
            }
            if (isVaeConfigValid) {
                updatedConfiguration = updatedConfiguration.setSerializedVaeConfig(
                    JSON.stringify(parsedValue)
                );
            }
        }
        if (distributionPlanConfig) {
            try {
                parsedValue = JSON.parse(distributionPlanConfig);
            } catch (e) {
                pjpConfigType = PjpConfigScreens[2];

                isDistributionPlanValid = false;
                this.setState({
                    error: t('pjpCreate.pjpConfig.invalidJson', {
                        jsonType: pjpConfigType
                    })
                });
            }
            if (isDistributionPlanValid) {
                let {
                    distributionPlanConfiguration: updatedDistributionPjpPlanConfig
                } = configuration;

                updatedDistributionPjpPlanConfig = updatedDistributionPjpPlanConfig.setSerializedVaeConfig(
                    JSON.stringify(parsedValue)
                );
                updatedConfiguration = updatedConfiguration.setDistributionPlanConfiguration(
                    updatedDistributionPjpPlanConfig
                );
            }
        }
        if (
            isSchedulingConfigValid &&
            isDistributionPlanValid &&
            isVaeConfigValid &&
            changePjpConfig
        ) {
            changePjpConfig(updatedConfiguration);
        }
    };

    onTabChange = index => {
        this.setState({
            activeTab: PjpConfigScreens[index],
            activeTabIndex: index
        });
    };

    render() {
        const {
            error,
            activeTab,
            serializedSchedulingConfig,
            serializedVaeConfig,
            distributionPlanConfig,
            activeTabIndex
        } = this.state;
        let editedValue = null;
        const { t, theme, isLoadingSchedulingConfigs } = this.props;
        if (activeTab === PjpConfigScreens[0]) {
            editedValue = serializedSchedulingConfig;
        }
        if (activeTab === PjpConfigScreens[1]) {
            editedValue = serializedVaeConfig;
        }
        if (activeTab === PjpConfigScreens[2]) {
            editedValue = distributionPlanConfig;
        }
        const isSaveDisabled =
            serializedSchedulingConfig ||
            serializedVaeConfig ||
            distributionPlanConfig
                ? false
                : true;
        if (isLoadingSchedulingConfigs) {
            return null;
        }
        return (
            <XyzzyDialog
                // eslint-disable-next-line react/jsx-no-bind
                render={dialog => {
                    return (
                        <div className={theme.wrap}>
                            <span className={theme.popupTitle}>
                                {t('pjpCreate.pjpConfig.customize')}
                            </span>
                            <CardTabs
                                theme={theme}
                                index={activeTabIndex}
                                onChange={this.onTabChange}
                            >
                                <CardTab
                                    activeClassName={theme.active}
                                    active={activeTabIndex === 0}
                                    key={0}
                                    theme={theme}
                                    title={t(
                                        'pjpCreate.pjpConfig.serializedSchedulingConfig'
                                    )}
                                />
                                <CardTab
                                    activeClassName={theme.active}
                                    active={activeTabIndex === 1}
                                    title={t(
                                        'pjpCreate.pjpConfig.serializedVaeConfig'
                                    )}
                                    theme={theme}
                                    key={1}
                                />
                                <CardTab
                                    activeClassName={theme.active}
                                    active={activeTabIndex === 2}
                                    title={t(
                                        'pjpCreate.pjpConfig.distributionPlanConfig'
                                    )}
                                    theme={theme}
                                    key={2}
                                />
                            </CardTabs>
                            <div className={theme.textArea}>
                                <textarea
                                    className={classnames(
                                        theme.input,
                                        'draggableCancel'
                                    )}
                                    spellCheck="false"
                                    type="text"
                                    value={editedValue || ''}
                                    onChange={this.onTextAreaChange}
                                />
                            </div>
                            <span className={theme.error}>{error}</span>
                            <div className={theme.actions}>
                                <Button
                                    flat
                                    label={t('reset')}
                                    onClick={this.onReset}
                                />
                                <Button
                                    flat
                                    label={t('close')}
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onClick={() => {
                                        this.onClose();
                                        dialog.hide();
                                    }}
                                />
                                <Button
                                    flat
                                    label={t('change')}
                                    disabled={isSaveDisabled}
                                    onClick={this.onSubmit}
                                />
                            </div>
                        </div>
                    );
                }}
            />
        );
    }
}

export default compose(themr('PjpDashboardConfig', theme))(PjpDashboardConfig);
